import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Wifi, WifiOff, AlertTriangle, Zap, Clock, Activity, Signal, TrendingUp, Plus, Trash2, Save, Settings, Edit2, Check, X, RefreshCw, ChevronDown, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { websocketService } from '../services/websocket';
import HistoricalChart from '../components/HistoricalChart';
import SensorRuleEditor from '../components/SensorRuleEditor';
import OTAManager from '../components/OTAManager';
import SensorManagerModal from '../components/SensorManagerModal';

function DeviceDetail() {
    const { id } = useParams();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [realtimeData, setRealtimeData] = useState({});
    const [selectedSensor, setSelectedSensor] = useState(null);
    const [showRuleEditor, setShowRuleEditor] = useState(false);
    const [showOTAManager, setShowOTAManager] = useState(false);
    const [showSensorManager, setShowSensorManager] = useState(false);
    const [renamingSensorId, setRenamingSensorId] = useState(null);
    const [newSensorName, setNewSensorName] = useState('');
    const [isRenamingDevice, setIsRenamingDevice] = useState(false);
    const [newDeviceName, setNewDeviceName] = useState('');
    const [showDeviceSettings, setShowDeviceSettings] = useState(false);
    const [showOTADropdown, setShowOTADropdown] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [targetLocationId, setTargetLocationId] = useState('');
    const [deviceConfig, setDeviceConfig] = useState({
        heartbeat_interval: 60,
        armed: false,
        ota_enabled: true,
        debug_mode: false
    });

    const { data: currentUserData } = useQuery(
        'current-user',
        () => apiService.getCurrentUser(),
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
            select: (data) => data?.user || data
        }
    );
    const currentUser = currentUserData;
    const canManageDevice = currentUser?.role === 'admin' || currentUser?.role === 'operator';

    const { data: device, isLoading } = useQuery(
        ['device', id],
        () => apiService.getDevice(id),
        {
            refetchOnWindowFocus: false, // Disable auto-refresh
            enabled: !!id,
            select: (data) => data.device
        }
    );

    const { data: locations = [] } = useQuery('locations', apiService.getLocations, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        select: (data) => data.locations || data || []
    });

    const { data: sensors = [], isLoading: sensorsLoading, error: sensorsError } = useQuery(
        ['device-sensors', id],
        () => apiService.getDeviceSensors(id),
        {
            enabled: !!id,
            refetchOnWindowFocus: false, // Disable auto-refresh
            onSuccess: (data) => {
                console.log('Sensors fetched successfully:', data);
            },
            onError: (error) => {
                console.error('Failed to fetch sensors:', error);
            },
            select: (data) => {
                if (!data) return [];
                if (Array.isArray(data.sensors)) return data.sensors;
                if (Array.isArray(data)) return data;
                return [];
            }
        }
    );

    useEffect(() => {
        if (!selectedSensor && sensors && sensors.length > 0) {
            setSelectedSensor(sensors[0]);
        }
    }, [sensors, selectedSensor]);

    const { data: stats = [] } = useQuery(
        ['device-stats', id],
        () => apiService.getDeviceStats(id, '24h'),
        {
            enabled: !!id,
            refetchOnWindowFocus: false, // Disable auto-refresh
            select: (data) => {
                // Ensure we always return an array of sensor stats
                if (!data) return [];
                // Backend returns { stats: { sensors: [...], alerts: {...}, device: {...} } }
                if (data.stats?.sensors && Array.isArray(data.stats.sensors)) {
                    return data.stats.sensors;
                }
                // Fallback for direct array
                if (Array.isArray(data.stats)) return data.stats;
                if (Array.isArray(data)) return data;
                return [];
            }
        }
    );

    const { data: alerts = [] } = useQuery(
        ['device-alerts', id],
        () => apiService.getDeviceAlerts(id),
        {
            enabled: !!id,
            refetchOnWindowFocus: false, // Disable auto-refresh
            select: (data) => data.alerts || data || []
        }
    );

    // Fetch latest telemetry data - relies on WebSocket for real-time updates
    const { data: latestTelemetry = {} } = useQuery(
        ['latest-telemetry', id],
        () => apiService.getLatestTelemetry(id),
        {
            enabled: !!id,
            staleTime: 5 * 1000, // 5 seconds - allow frequent updates but not on every render
            select: (data) => {
                // Convert array of telemetry to object keyed by pin for easy lookup
                const telemetryByPin = {};
                const telemetryData = data?.telemetry || data || [];
                if (Array.isArray(telemetryData)) {
                    telemetryData.forEach(reading => {
                        if (reading.sensor_pin) {
                            telemetryByPin[reading.sensor_pin] = {
                                value: reading.processed_value || reading.raw_value,
                                raw_value: reading.raw_value,
                                processed_value: reading.processed_value,
                                timestamp: reading.timestamp,
                                unit: reading.unit,
                                sensor_name: reading.sensor_name
                            };
                        }
                    });
                }
                return telemetryByPin;
            }
        }
    );

    const updateSensorMutation = useMutation(
        ({ deviceId, sensorId, payload }) => apiService.updateSensor(deviceId, sensorId, payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['device-sensors', id]);
                toast.success(t('deviceDetail.sensorUpdated', 'Sensor updated successfully'));
            },
            onError: (error) => {
                toast.error(error?.response?.data?.error || t('deviceDetail.sensorUpdateFailed', 'Failed to update sensor'));
            }
        }
    );

    const updateDeviceMutation = useMutation(
        ({ deviceId, payload }) => apiService.updateDevice(deviceId, payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['device', id]);
                toast.success(t('deviceDetail.deviceUpdated', 'Device updated successfully'));
            },
            onError: (error) => {
                toast.error(error?.response?.data?.error || t('deviceDetail.deviceUpdateFailed', 'Failed to update device'));
            }
        }
    );

    const changeLocationMutation = useMutation(
        ({ deviceId, locationId }) => apiService.updateDevice(deviceId, { location_id: locationId }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['device', id]);
                queryClient.invalidateQueries('locations');
                toast.success(t('deviceDetail.locationChanged', 'Device location changed successfully'));
                setShowLocationModal(false);
                setTargetLocationId('');
            },
            onError: () => {
                toast.error(t('deviceDetail.locationChangeError', 'Failed to change device location'));
            }
        }
    );

    const otaRebuildMutation = useMutation(
        (deviceId) => apiService.otaRebuild(deviceId),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(['device', id]);
                toast.success(
                    <div>
                        <strong>{t('deviceDetail.toast.otaRebuildStarted', 'Firmware rebuild initiated!')}</strong>
                        <br />
                        {data.message}
                        <br />
                        <small>{t('deviceDetail.toast.otaRebuildSensors', { count: data.sensors_configured })}</small>
                    </div>,
                    { duration: 6000 }
                );
            },
            onError: (error) => {
                toast.error(error?.response?.data?.error || t('deviceDetail.toast.otaRebuildError', 'Failed to initiate firmware rebuild'));
            }
        }
    );

    const updateDeviceConfigMutation = useMutation(
        ({ deviceId, config }) => apiService.updateDeviceConfig(deviceId, config),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['device', id]);
                toast.success(t('deviceDetail.configUpdated', 'Configuration updated. Device will receive changes on next heartbeat.'));
                setShowDeviceSettings(false);
            },
            onError: (error) => {
                toast.error(error?.response?.data?.error || t('deviceDetail.configUpdateFailed', 'Failed to update configuration'));
            }
        }
    );

    const ensureCanManage = () => {
        if (!canManageDevice) {
            toast.error(t('deviceDetail.permissionDenied', 'You do not have permission to manage this device.'));
            return false;
        }
        return true;
    };

    const handleStartRename = (sensor) => {
        if (!ensureCanManage()) {
            return;
        }
        setRenamingSensorId(sensor.id);
        setNewSensorName(sensor.name || '');
    };

    const handleCancelRename = () => {
        setRenamingSensorId(null);
        setNewSensorName('');
    };

    const handleSaveRename = async (sensorId) => {
        if (!ensureCanManage()) {
            return;
        }
        if (!newSensorName.trim()) {
            toast.error(t('deviceDetail.sensorNameRequired', 'Sensor name cannot be empty'));
            return;
        }

        try {
            await updateSensorMutation.mutateAsync({
                deviceId: id,
                sensorId: sensorId,
                payload: { name: newSensorName.trim() }
            });
            setRenamingSensorId(null);
            setNewSensorName('');
        } catch (error) {
            // Error already handled in mutation
        }
    };

    const handleStartDeviceRename = () => {
        if (!ensureCanManage()) {
            return;
        }
        setIsRenamingDevice(true);
        setNewDeviceName(device?.name || '');
    };

    const handleCancelDeviceRename = () => {
        setIsRenamingDevice(false);
        setNewDeviceName('');
    };

    const handleSaveDeviceRename = async () => {
        if (!ensureCanManage()) {
            return;
        }
        if (!newDeviceName.trim()) {
            toast.error(t('deviceDetail.deviceNameRequired', 'Device name cannot be empty'));
            return;
        }

        try {
            await updateDeviceMutation.mutateAsync({
                deviceId: id,
                payload: { name: newDeviceName.trim() }
            });
            setIsRenamingDevice(false);
            setNewDeviceName('');
        } catch (error) {
            // Error already handled in mutation
        }
    };

    const handleOpenDeviceSettings = () => {
        if (!ensureCanManage()) {
            return;
        }
        // Load current config from device
        setDeviceConfig({
            heartbeat_interval: device?.heartbeat_interval || 60,
            sensor_read_interval: device?.sensor_read_interval || 1000,
            armed: device?.armed || false,
            ota_enabled: device?.ota_enabled !== false,
            debug_mode: device?.debug_mode || false
        });
        setShowDeviceSettings(true);
    };

    const handleSaveDeviceConfig = async () => {
        if (!ensureCanManage()) {
            return;
        }
        try {
            await updateDeviceConfigMutation.mutateAsync({
                deviceId: id,
                config: deviceConfig
            });
        } catch (error) {
            // Error already handled in mutation
        }
    };

    const handleOpenSensorManager = () => {
        if (!ensureCanManage()) {
            return;
        }
        setShowSensorManager(true);
    };

    const handleOpenRuleEditor = (sensorToEdit) => {
        if (!ensureCanManage()) {
            return;
        }
        if (!sensorToEdit) {
            toast.error(t('deviceDetail.manageRulesEmpty'));
            return;
        }
        setSelectedSensor(sensorToEdit);
        setShowRuleEditor(true);
    };

    const handleOpenOtaManager = () => {
        if (!ensureCanManage()) {
            return;
        }
        setShowOTAManager(true);
    };

    const handleTriggerOtaRebuild = () => {
        if (!ensureCanManage()) {
            return;
        }
        otaRebuildMutation.mutate(id);
    };

    useEffect(() => {
        if (!canManageDevice) {
            setShowSensorManager(false);
            setShowRuleEditor(false);
            setShowOTAManager(false);
            setShowDeviceSettings(false);
            setShowOTADropdown(false);
            setIsRenamingDevice(false);
            setRenamingSensorId(null);
            setNewDeviceName('');
            setNewSensorName('');
        }
    }, [canManageDevice]);

    useEffect(() => {
        if (!id) return;

        const handleTelemetryUpdate = (data) => {
            setRealtimeData((prev) => ({
                ...prev,
                [data.pin]: {
                    ...data,
                    timestamp: new Date(data.timestamp)
                }
            }));
        };

        const handleDeviceUpdate = (data) => {
            queryClient.setQueryData(['device', id], (old) => ({
                ...old,
                ...data
            }));
        };

        const handleConfigUpdate = () => {
            queryClient.invalidateQueries(['device', id]);
        };

        if (websocketService && typeof websocketService.subscribe === 'function') {
            websocketService.subscribe('device', id);
            websocketService.on(`device:${id}:telemetry`, handleTelemetryUpdate);
            websocketService.on(`device:${id}:updated`, handleDeviceUpdate);
            websocketService.on(`device:${id}:config_updated`, handleConfigUpdate);
        }

        return () => {
            if (websocketService && typeof websocketService.unsubscribe === 'function') {
                websocketService.unsubscribe('device', id);
                websocketService.off(`device:${id}:telemetry`, handleTelemetryUpdate);
                websocketService.off(`device:${id}:updated`, handleDeviceUpdate);
                websocketService.off(`device:${id}:config_updated`, handleConfigUpdate);
            }
        };
    }, [id, queryClient]);

    const formatRelativeTime = (date) => {
        if (!date) return t('deviceDetail.relative.never', 'Never');
        const value = typeof date === 'string' ? new Date(date) : date;
        const diff = Date.now() - value.getTime();
        if (diff < 0) return t('deviceDetail.relative.justNow', 'Just now');
        const minutes = Math.floor(diff / (1000 * 60));
        if (minutes < 1) {
            return t('deviceDetail.relative.justNow', 'Just now');
        }

        if (minutes < 60) {
            return t('deviceDetail.relative.minutes', '{{count}} min ago', {
                count: minutes
            });
        }

        const hours = Math.floor(minutes / 60);
        if (hours < 24) {
            return t('deviceDetail.relative.hours', '{{hours}}h {{minutes}}m ago', {
                hours,
                minutes: minutes % 60
            });
        }

        const days = Math.floor(hours / 24);
        if (days < 7) {
            return t(`deviceDetail.relative.days_${days === 1 ? 'one' : 'other'}`, '{{count}} day(s) ago', {
                count: days
            });
        }

        return value.toLocaleString();
    };

    const formatDuration = (seconds) => {
        if (!seconds && seconds !== 0) return '—';
        const totalMinutes = Math.floor(seconds / 60);
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const formatSignalStrength = (value) => {
        if (value === null || value === undefined) return '—';
        return `${value} dBm`;
    };

    const getSignalQualityLabel = (value) => {
        if (value === null || value === undefined) {
            return t('deviceDetail.signalQuality.none', 'No data');
        }
        if (value >= -55) return t('deviceDetail.signalQuality.excellent', 'Excellent');
        if (value >= -65) return t('deviceDetail.signalQuality.veryGood', 'Very good');
        if (value >= -75) return t('deviceDetail.signalQuality.fair', 'Fair');
        if (value >= -90) return t('deviceDetail.signalQuality.weak', 'Weak');
        return t('deviceDetail.signalQuality.poor', 'Poor');
    };

    const statsByPin = useMemo(() => {
        const map = new Map();
        // Ensure stats is an array before calling forEach
        const statsArray = Array.isArray(stats) ? stats : [];
        statsArray.forEach((entry) => {
            if (entry && entry.pin) {
                map.set(entry.pin, entry);
            }
        });
        return map;
    }, [stats]);

    const sortedSensors = useMemo(() => {
        return Array.isArray(sensors)
            ? [...sensors].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            : [];
    }, [sensors]);

    const activeAlerts = useMemo(() => (Array.isArray(alerts) ? alerts.slice(0, 5) : []), [alerts]);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'online':
                return <Activity className="h-12 w-12 text-white" />;
            case 'offline':
                return <Activity className="h-12 w-12 text-white opacity-50" />;
            case 'alarm':
                return <AlertTriangle className="h-12 w-12 text-white" />;
            default:
                return <Activity className="h-12 w-12 text-white opacity-50" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'online':
                return 'bg-green-100 text-green-800';
            case 'offline':
                return 'bg-gray-100 text-gray-800';
            case 'alarm':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatStatusLabel = (status) => {
        if (!status) {
            return t('deviceDetail.status.unknown');
        }
        return t(`deviceDetail.status.${status}`, { defaultValue: status });
    };

    const getSensorStateBadge = (state, enabled) => {
        if (!enabled) {
            return { label: t('deviceDetail.sensorBadge.disabled', 'Disabled'), className: 'bg-red-100 text-red-700' };
        }
        switch (state) {
            case 'fresh':
                return { label: t('deviceDetail.sensorBadge.live'), className: 'bg-emerald-100 text-emerald-700' };
            case 'stale':
                return { label: t('deviceDetail.sensorBadge.stale'), className: 'bg-amber-100 text-amber-700' };
            default:
                return { label: t('deviceDetail.sensorBadge.awaiting'), className: 'bg-gray-100 text-gray-600' };
        }
    };

    const formatStat = (value) => {
        const numeric = parseFloat(value);
        return Number.isFinite(numeric) ? numeric.toFixed(2) : '—';
    };

    const adjustForUnit = (sensor, value) => {
        if (value === null || value === undefined) return null;
        const numeric = typeof value === 'number' ? value : parseFloat(value);
        if (!Number.isFinite(numeric)) return null;

        // Return value as-is without any adjustment
        return numeric;
    };

    const normalizeNumeric = (value) => {
        if (value === null || value === undefined) return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const normalizeTimestamp = (input) => {
        if (!input) return null;
        if (typeof input === 'number') {
            // Some firmware reports uptime seconds; treat values < 1e12 as seconds
            const millis = input < 1e12 ? input * 1000 : input;
            return new Date(millis);
        }
        const date = new Date(input);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const getSensorDisplayData = (sensor) => {
        // Priority: WebSocket realtime > Latest telemetry API > Stats
        const realtime = realtimeData[sensor.pin] || latestTelemetry[sensor.pin];
        const stat = statsByPin.get(sensor.pin);
        const candidateValue = realtime
            ? (realtime.processed_value ?? realtime.value ?? realtime.raw_value)
            : (stat?.avg_value ?? null);
        const value = adjustForUnit(sensor, normalizeNumeric(candidateValue));
        const timestamp = normalizeTimestamp(realtime?.timestamp);
        const state = realtime && timestamp
            ? (Date.now() - timestamp.getTime() <= 120000 ? 'fresh' : 'stale')
            : 'unknown';

        const normalizedStat = stat ? {
            ...stat,
            avg_value: adjustForUnit(sensor, normalizeNumeric(stat.avg_value)),
            min_value: adjustForUnit(sensor, normalizeNumeric(stat.min_value)),
            max_value: adjustForUnit(sensor, normalizeNumeric(stat.max_value))
        } : null;

        return {
            value,
            timestamp,
            state,
            unit: realtime?.unit || sensor.unit || normalizedStat?.unit || '',
            stat: normalizedStat
        };
    };

    const lastHeartbeatLabel = formatRelativeTime(device?.last_heartbeat ? new Date(device.last_heartbeat) : null);
    // Use total_runtime_seconds for total runtime, uptime_seconds for current session
    const totalRuntimeLabel = formatDuration(device?.total_runtime_seconds || device?.uptime_seconds);
    const totalRuntimeHours = Math.floor((device?.total_runtime_seconds || 0) / 3600);
    const currentUptimeLabel = formatDuration(device?.uptime_seconds);
    const wifiStrengthLabel = formatSignalStrength(device?.wifi_signal_strength);
    const signalQualityLabel = getSignalQualityLabel(device?.wifi_signal_strength);
    const statusLabel = formatStatusLabel(device?.status);
    const targetFirmware = device?.target_firmware_version && device?.target_firmware_version !== device?.firmware_version
        ? device.target_firmware_version
        : null;

    const deviceMetrics = [
        {
            label: t('deviceDetail.metrics.status'),
            value: statusLabel,
            icon: Activity,
            hint: t('deviceDetail.metrics.statusHint', { when: lastHeartbeatLabel })
        },
        {
            label: t('deviceDetail.metrics.totalRuntime', 'Total Runtime'),
            value: totalRuntimeLabel,
            icon: Clock,
            hint: device?.total_runtime_seconds
                ? t('deviceDetail.metrics.runtimeHint', {
                    hours: totalRuntimeHours,
                    current: currentUptimeLabel,
                    defaultValue: `${totalRuntimeHours} total hours (current session: ${currentUptimeLabel})`
                })
                : t('deviceDetail.metrics.uptimeUnknown')
        },
        {
            label: t('deviceDetail.metrics.signal'),
            value: wifiStrengthLabel,
            icon: Signal,
            hint: signalQualityLabel
        },
        {
            label: t('deviceDetail.metrics.firmware'),
            value: device?.firmware_version || t('deviceDetail.metrics.firmwareUnknown'),
            icon: TrendingUp,
            hint: targetFirmware
                ? t('deviceDetail.metrics.firmwareTarget', { version: targetFirmware })
                : t('deviceDetail.metrics.firmwareCurrent')
        }
    ];

    const severityColors = {
        low: 'bg-emerald-50 text-emerald-700',
        medium: 'bg-amber-50 text-amber-700',
        high: 'bg-orange-50 text-orange-700',
        critical: 'bg-red-50 text-red-700'
    };

    const formatSeverityLabel = (severity) => {
        if (!severity) {
            return t('deviceDetail.alertSeverity.info', 'Info');
        }
        const key = `deviceDetail.alertSeverity.${severity.toLowerCase?.() || 'info'}`;
        return t(key, severity.toUpperCase());
    };

    const handleSensorHistory = (sensor) => {
        if (!sensor) return;
        setSelectedSensor(sensor);
        const historySection = document.getElementById('sensor-history');
        if (historySection) {
            historySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    if (isLoading) {
        return <div className="p-6">{t('deviceDetail.loadingDevice')}</div>;
    }

    if (!device) {
        return <div className="p-6">{t('deviceDetail.notFound')}</div>;
    }

    return (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
            <div className="space-y-4 sm:space-y-6">
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-200 via-indigo-200 to-purple-300 shadow-lg">
                    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,_#a855f7,_transparent_45%)]" />
                    <div className="relative z-10 flex flex-col gap-4 sm:gap-6 p-4 sm:p-6 lg:p-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6">
                            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                                <div className="flex-shrink-0">
                                    {getStatusIcon(device.status)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="uppercase text-xs tracking-[0.3em] text-indigo-900">{t('deviceDetail.eyebrow')}</p>
                                    {isRenamingDevice ? (
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <input
                                                type="text"
                                                value={newDeviceName}
                                                onChange={(e) => setNewDeviceName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveDeviceRename();
                                                    if (e.key === 'Escape') handleCancelDeviceRename();
                                                }}
                                                className="text-lg sm:text-2xl font-semibold bg-white border-2 border-indigo-300 rounded px-2 sm:px-3 py-1 focus:outline-none focus:border-indigo-500 flex-1 min-w-[200px] text-gray-900"
                                                placeholder={t('deviceDetail.deviceNamePlaceholder', 'Device name')}
                                                autoFocus
                                            />
                                            <button
                                                onClick={handleSaveDeviceRename}
                                                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded flex-shrink-0"
                                                title={t('deviceDetail.save', 'Save')}
                                            >
                                                <Check className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={handleCancelDeviceRename}
                                                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded flex-shrink-0"
                                                title={t('deviceDetail.cancel', 'Cancel')}
                                            >
                                                <X className="h-5 w-5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 min-w-0">
                                            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-indigo-900 truncate">{device.name}</h1>
                                            {canManageDevice && (
                                                <button
                                                    onClick={handleStartDeviceRename}
                                                    className="p-1 hover:bg-indigo-200 rounded transition-colors flex-shrink-0"
                                                    title={t('deviceDetail.rename', 'Rename device')}
                                                >
                                                    <Edit2 className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-700 hover:text-indigo-900" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm sm:text-base text-indigo-700 truncate">
                                            {device.location_name || t('deviceDetail.unassigned')}
                                        </p>
                                        {canManageDevice && (
                                            <button
                                                onClick={() => {
                                                    if (!ensureCanManage()) return;
                                                    setTargetLocationId(device.location_id?.toString() || '');
                                                    setShowLocationModal(true);
                                                }}
                                                className="p-1 hover:bg-indigo-200 rounded transition-colors flex-shrink-0"
                                                title={t('deviceDetail.changeLocation', 'Change location')}
                                            >
                                                <MapPin className="h-4 w-4 text-indigo-700 hover:text-indigo-900" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {canManageDevice ? (
                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    <button
                                        onClick={handleOpenSensorManager}
                                        className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-indigo-300 bg-white/70 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-indigo-900 hover:bg-white transition-colors"
                                    >
                                        <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        <span className="hidden sm:inline">{t('deviceDetail.manageSensors', 'Manage Sensors')}</span>
                                        <span className="sm:hidden">{t('deviceDetail.sensors', 'Sensors')}</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (sortedSensors.length === 0) {
                                                toast.error(t('deviceDetail.manageRulesEmpty'));
                                                return;
                                            }
                                            handleOpenRuleEditor(sortedSensors[0]);
                                        }}
                                        className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-indigo-300 bg-white/70 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-indigo-900 hover:bg-white transition-colors"
                                    >
                                        <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        <span className="hidden sm:inline">{t('deviceDetail.manageRules')}</span>
                                        <span className="sm:hidden">{t('deviceDetail.rules', 'Rules')}</span>
                                    </button>
                                    <button
                                        onClick={handleOpenDeviceSettings}
                                        className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-indigo-300 bg-white/70 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-indigo-900 hover:bg-white transition-colors"
                                    >
                                        <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        <span className="hidden sm:inline">{t('deviceDetail.deviceSettings', 'Device Settings')}</span>
                                        <span className="sm:hidden">{t('deviceDetail.settings', 'Settings')}</span>
                                    </button>
                                    <div className="relative">
                                        <button
                                            onClick={() => {
                                                if (!ensureCanManage()) return;
                                                setShowOTADropdown(prev => !prev);
                                            }}
                                            className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-indigo-600 shadow hover:bg-indigo-50 transition-colors"
                                        >
                                            <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                            <span className="hidden sm:inline">{t('deviceDetail.otaUpdate', 'OTA Update')}</span>
                                            <span className="sm:hidden">{t('deviceDetail.otaLabel', 'OTA')}</span>
                                            <ChevronDown className={`h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform ${showOTADropdown ? 'rotate-180' : ''}`} />
                                        </button>
                                        {showOTADropdown && (
                                            <>
                                                <div
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setShowOTADropdown(false)}
                                                />
                                                <div className="absolute right-0 mt-2 w-64 sm:w-72 rounded-lg bg-white shadow-xl border border-gray-200 overflow-hidden z-20">
                                                    <button
                                                        onClick={() => {
                                                            setShowOTADropdown(false);
                                                            handleOpenOtaManager();
                                                        }}
                                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3"
                                                    >
                                                        <Zap className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-medium text-gray-900">{t('deviceDetail.otaMenu.manualTitle', 'Manual OTA Update')}</div>
                                                            <div className="text-xs text-gray-500 mt-0.5">{t('deviceDetail.otaMenu.manualSubtitle', 'Upload and push firmware file')}</div>
                                                        </div>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShowOTADropdown(false);
                                                            if (confirm(t('deviceDetail.otaMenu.rebuildConfirm', 'This will rebuild the firmware with current device configuration and push it via OTA. The device will automatically reboot with the new firmware. Continue?'))) {
                                                                handleTriggerOtaRebuild();
                                                            }
                                                        }}
                                                        disabled={otaRebuildMutation.isLoading || device.status !== 'online'}
                                                        className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors flex items-start gap-3 disabled:opacity-50 disabled:cursor-not-allowed border-t border-gray-100"
                                                        title={device.status !== 'online' ? t('deviceDetail.otaMenu.tooltipOffline', 'Device must be online for OTA rebuild') : t('deviceDetail.otaMenu.tooltipReady', 'Rebuild firmware and push via OTA')}
                                                    >
                                                        <RefreshCw className={`h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0 ${otaRebuildMutation.isLoading ? 'animate-spin' : ''}`} />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-medium text-gray-900">
                                                                {otaRebuildMutation.isLoading ? t('deviceDetail.otaMenu.rebuildLoading', 'Rebuilding...') : t('deviceDetail.otaMenu.rebuildTitle', 'Rebuild & OTA Update')}
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-0.5">{t('deviceDetail.otaMenu.rebuildSubtitle', 'Auto-rebuild with current config')}</div>
                                                        </div>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg bg-white/70 px-3 py-2 text-xs font-medium text-indigo-900 border border-white/70 shadow-sm">
                                    {t('deviceDetail.viewOnlyNotice', 'You have view-only access to this device. Contact an administrator to manage it.')}
                                </div>
                            )}
                        </div>
                        <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
                            <div className="rounded-lg sm:rounded-xl bg-white/20 p-3 sm:p-4 backdrop-blur border border-white/30">
                                <p className="text-xs sm:text-sm text-indigo-900 font-medium">{t('devices.deviceId')}</p>
                                <p className="text-sm sm:text-lg font-semibold break-all line-clamp-2 text-indigo-950">{device.id}</p>
                                <p className="mt-1 text-xs text-indigo-700">{t('deviceDetail.metrics.statusHint', { when: lastHeartbeatLabel })}</p>
                            </div>
                            <div className="rounded-lg sm:rounded-xl bg-white/20 p-3 sm:p-4 backdrop-blur border border-white/30">
                                <p className="text-xs sm:text-sm text-indigo-900 font-medium">{t('deviceDetail.ipAddress')}</p>
                                <p className="text-sm sm:text-lg font-semibold truncate text-indigo-950">{device.ip_address || t('deviceDetail.valueUnknown')}</p>
                                <p className="mt-1 text-xs text-indigo-700">{t('deviceDetail.metrics.networkInfo', 'Network Address')}</p>
                            </div>
                            <div className="rounded-lg sm:rounded-xl bg-white/20 p-3 sm:p-4 backdrop-blur border border-white/30">
                                <p className="text-xs sm:text-sm text-indigo-900 font-medium">{t('deviceDetail.macAddress', 'MAC Address')}</p>
                                <p className="text-sm sm:text-lg font-semibold truncate text-indigo-950">{device.mac_address || t('deviceDetail.valueUnknown')}</p>
                                <p className="mt-1 text-xs text-indigo-700">{device.mac_address?.startsWith('DE:') ? t('deviceDetail.macCustom', 'Sensity Custom MAC') : t('deviceDetail.macHardware', 'Hardware MAC')}</p>
                            </div>
                            <div className="rounded-lg sm:rounded-xl bg-white/20 p-3 sm:p-4 backdrop-blur border border-white/30">
                                <p className="text-xs sm:text-sm text-indigo-900 font-medium">{t('deviceDetail.metrics.uptime', 'Uptime')}</p>
                                <p className="text-sm sm:text-lg font-semibold text-indigo-950">{totalRuntimeLabel}</p>
                                <p className="mt-1 text-xs text-indigo-700">{t('deviceDetail.metrics.totalRuntime', 'Total Runtime')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invalid IP Address Warning */}
                {device?.ip_address && ['127.0.0.1', '0.0.0.0', 'localhost', '::1'].includes(device.ip_address) && (
                    <div className="rounded-lg sm:rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4 shadow-sm">
                        <div className="flex items-start gap-2 sm:gap-3">
                            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-amber-900 text-sm sm:text-base">{t('deviceDetail.ipWarning.title', 'Invalid IP Address Detected')}</h3>
                                <p className="mt-1 text-xs sm:text-sm text-amber-800">
                                    {t('deviceDetail.ipWarning.description', 'Your device is showing an invalid local IP address ({{ip}}). This usually means the device hasn\'t connected to your WiFi network yet.', { ip: device.ip_address })}
                                </p>
                                <ul className="mt-2 ml-4 list-disc space-y-1 text-xs sm:text-sm text-amber-700">
                                    <li>{t('deviceDetail.ipWarning.step1', 'Make sure your WiFi credentials are correct in the firmware')}</li>
                                    <li>{t('deviceDetail.ipWarning.step2', 'Restart the device to attempt WiFi connection')}</li>
                                    <li>{t('deviceDetail.ipWarning.step3', 'Check your router to see if the device is connected')}</li>
                                    <li>{t('deviceDetail.ipWarning.step4', 'The IP will update automatically once the device connects and sends a heartbeat')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid gap-4 sm:gap-6 lg:grid-cols-[2fr_1fr]">
                    <div className="space-y-4 sm:space-y-6 min-w-0">
                        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                            {deviceMetrics.map(({ label, value, icon: Icon, hint }) => (
                                <div key={label} className="flex items-start gap-3 sm:gap-4 rounded-lg sm:rounded-xl border border-gray-100 bg-white p-3 sm:p-4 shadow-sm min-w-0">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 flex-shrink-0">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs sm:text-sm text-gray-500">{label}</p>
                                        <p className="text-base sm:text-lg font-semibold text-gray-900 truncate">{value || '—'}</p>
                                        <p className="mt-1 text-xs text-gray-400 truncate">{hint}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <section className="space-y-4">
                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                        <Activity className="h-5 w-5 text-indigo-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-900">{t('deviceDetail.sensorSectionTitle')}</h2>
                                        <p className="text-sm text-gray-500">
                                            {sortedSensors.length
                                                ? t('deviceDetail.sensorSectionDescription')
                                                : t('deviceDetail.sensorSectionEmptyHint')}
                                        </p>
                                    </div>
                                </div>
                                {sortedSensors.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => {
                                                queryClient.invalidateQueries(['device-sensors', id]);
                                                queryClient.invalidateQueries(['device', id]);
                                                toast.success(t('deviceDetail.toast.refreshingData', 'Refreshing device data...'));
                                            }}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                        >
                                            <Activity className="h-4 w-4" />
                                            {t('deviceDetail.refresh', 'Refresh')}
                                        </button>
                                        <button
                                            onClick={() => handleSensorHistory(selectedSensor || sortedSensors[0])}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                        >
                                            {t('deviceDetail.viewHistory', 'View history')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3">
                                {sensorsLoading ? (
                                    <div className="col-span-full flex flex-col items-center justify-center rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 p-12 text-center">
                                        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent"></div>
                                        <p className="mt-4 text-sm text-indigo-500">{t('deviceDetail.loadingSensors')}</p>
                                    </div>
                                ) : sensorsError ? (
                                    <div className="col-span-full rounded-xl border border-red-200 bg-red-50 p-6 text-center">
                                        <p className="font-medium text-red-600">{t('deviceDetail.loadSensorsError')}</p>
                                        <p className="mt-1 text-sm text-red-500">{sensorsError.message}</p>
                                    </div>
                                ) : !sortedSensors.length ? (
                                    <div className="col-span-full rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
                                        <p className="font-medium text-gray-700">{t('deviceDetail.sensorSectionEmpty')}</p>
                                        <p className="mt-1 text-sm text-gray-500">{t('deviceDetail.sensorSectionEmptyHint')}</p>
                                        {activeAlerts.length > 0 && (
                                            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg mx-auto max-w-md">
                                                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-600" />
                                                <p className="text-sm font-semibold text-red-900">{t('deviceDetail.sensorDataDetectedTitle', '⚠️ Sensor Data Detected')}</p>
                                                <p className="mt-1 text-xs text-red-800">
                                                    {t('deviceDetail.sensorDataDetectedBody', 'Your device is sending sensor alerts, but sensors are not showing in the UI. Try refreshing or adding sensors manually.')}
                                                </p>
                                            </div>
                                        )}
                                        <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                                            <button
                                                onClick={() => {
                                                    queryClient.invalidateQueries(['device-sensors', id]);
                                                    toast.success(t('deviceDetail.toast.refreshingSensors', 'Refreshing sensors...'));
                                                }}
                                                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                            >
                                                <Activity className="h-4 w-4" />
                                                {t('deviceDetail.refreshSensors', 'Refresh Sensors')}
                                            </button>
                                            {canManageDevice ? (
                                                <button
                                                    onClick={handleOpenSensorManager}
                                                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium text-white hover:bg-indigo-700 transition-colors shadow-lg"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                    {t('deviceDetail.manageSensors', 'Configure Sensors Now')}
                                                </button>
                                            ) : (
                                                <p className="text-xs text-gray-500 max-w-xs">
                                                    {t('deviceDetail.viewOnlySensors', 'You need elevated permissions to configure sensors on this device.')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    sortedSensors.map((sensor) => {
                                        const display = getSensorDisplayData(sensor);
                                        const badge = getSensorStateBadge(display.state, sensor.enabled);
                                        const isActive = selectedSensor?.id === sensor.id;
                                        const isDisabled = sensor.enabled === false;

                                        return (
                                            <div
                                                key={sensor.id}
                                                className={`relative flex h-full flex-col gap-3 sm:gap-4 rounded-lg sm:rounded-xl border bg-white p-4 sm:p-5 overflow-hidden ${isDisabled ? 'opacity-60 border-gray-200' :
                                                    isActive ? 'border-indigo-400 shadow-lg ring-2 ring-indigo-100' : 'border-gray-200 shadow-sm hover:shadow-lg'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between gap-2 min-w-0">
                                                    <div className="flex-1 min-w-0">
                                                        {renamingSensorId === sensor.id ? (
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="text"
                                                                    value={newSensorName}
                                                                    onChange={(e) => setNewSensorName(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') handleSaveRename(sensor.id);
                                                                        if (e.key === 'Escape') handleCancelRename();
                                                                    }}
                                                                    className="flex-1 min-w-0 rounded-lg border border-indigo-300 px-3 py-1.5 text-sm font-semibold focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                                                    placeholder={t('deviceDetail.sensorNamePlaceholder', 'Enter sensor name')}
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={() => handleSaveRename(sensor.id)}
                                                                    className="rounded-full p-1.5 bg-green-100 text-green-700 hover:bg-green-200 flex-shrink-0"
                                                                    title={t('deviceDetail.saveName', 'Save')}
                                                                >
                                                                    <Check className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={handleCancelRename}
                                                                    className="rounded-full p-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 flex-shrink-0"
                                                                    title={t('deviceDetail.cancel', 'Cancel')}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                                                                    {sensor.name || t('deviceDetail.defaultSensorName', { pin: sensor.pin })}
                                                                    {isDisabled && <span className="ml-2 text-sm font-normal text-red-600">({t('deviceDetail.notConfigured', 'Not Configured')})</span>}
                                                                </h3>
                                                                {canManageDevice && (
                                                                    <button
                                                                        onClick={() => handleStartRename(sensor)}
                                                                        className="rounded-full p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 flex-shrink-0"
                                                                        title={t('deviceDetail.renameSensor', 'Rename sensor')}
                                                                    >
                                                                        <Edit2 className="h-3.5 w-3.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                        <p className="text-xs text-gray-500 mt-1 truncate">{t('deviceDetail.sensorPinType', { pin: sensor.pin, type: sensor.sensor_type || sensor.type })}</p>
                                                        {isDisabled && (
                                                            <p className="mt-1 text-xs text-amber-600">
                                                                {t('deviceDetail.sensorDisabledHint', 'Use "Manage Sensors" to configure and enable this sensor')}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${badge.className}`}>
                                                            {badge.label}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex items-end gap-2 sm:gap-3">
                                                    <span className="text-2xl sm:text-3xl font-semibold text-gray-900 truncate">
                                                        {display.value !== null && display.value !== undefined && Number.isFinite(display.value)
                                                            ? display.value.toFixed(2)
                                                            : '—'}
                                                    </span>
                                                    {display.unit && <span className="text-sm text-gray-500 flex-shrink-0">{display.unit}</span>}
                                                </div>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {display.timestamp
                                                        ? t('deviceDetail.lastUpdated', { when: formatRelativeTime(display.timestamp) })
                                                        : t('deviceDetail.awaitingFirstReading')}
                                                </p>

                                                {!display.timestamp && sensor.enabled && (
                                                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs">
                                                        <p className="font-medium text-amber-800">{t('deviceDetail.troubleshooting.title', 'Troubleshooting:')}</p>
                                                        <ul className="mt-1 list-disc list-inside text-amber-700 space-y-1">
                                                            <li>{t('deviceDetail.troubleshooting.checkConnection', 'Check device is online and connected')}</li>
                                                            <li>{t('deviceDetail.troubleshooting.checkWiring', 'Verify sensor is connected to pin {{pin}}', { pin: sensor.pin })}</li>
                                                            <li>{t('deviceDetail.troubleshooting.waitHeartbeat', 'Wait for next heartbeat (~5 min)')}</li>
                                                            <li>{t('deviceDetail.troubleshooting.checkLogs', 'Check device serial logs for errors')}</li>
                                                        </ul>
                                                    </div>
                                                )}

                                                {display.stat && (
                                                    <div className="grid grid-cols-3 gap-2 sm:gap-3 rounded-lg border border-gray-100 bg-gray-50 p-2 sm:p-3 text-sm">
                                                        <div className="min-w-0">
                                                            <p className="text-xs text-gray-500">{t('deviceDetail.metricMin')}</p>
                                                            <p className="font-medium text-gray-900 truncate">{formatStat(display.stat.min_value)}</p>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-xs text-gray-500">{t('deviceDetail.metricAvg')}</p>
                                                            <p className="font-medium text-gray-900 truncate">{formatStat(display.stat.avg_value)}</p>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-xs text-gray-500">{t('deviceDetail.metricMax')}</p>
                                                            <p className="font-medium text-gray-900 truncate">{formatStat(display.stat.max_value)}</p>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="mt-auto flex flex-wrap gap-2 pt-2 text-xs sm:text-sm">
                                                    {canManageDevice && (
                                                        <button
                                                            onClick={() => handleOpenRuleEditor(sensor)}
                                                            className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-indigo-200 px-2.5 sm:px-3 py-1 sm:py-1.5 font-medium text-indigo-600 hover:bg-indigo-50 whitespace-nowrap"
                                                        >
                                                            <span className="hidden sm:inline">{t('deviceDetail.manageRules')}</span>
                                                            <span className="sm:hidden">{t('deviceDetail.rules', 'Rules')}</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleSensorHistory(sensor)}
                                                        className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-gray-200 px-2.5 sm:px-3 py-1 sm:py-1.5 font-medium text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                                                    >
                                                        <span className="hidden sm:inline">{t('deviceDetail.viewHistory')}</span>
                                                        <span className="sm:hidden">{t('deviceDetail.history', 'History')}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </section>

                        <section id="sensor-history" className="space-y-4">
                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">{t('deviceDetail.historyTitle')}</h2>
                                    <p className="text-sm text-gray-500">
                                        {selectedSensor
                                            ? t('deviceDetail.historySubtitle', { sensor: selectedSensor.name || t('deviceDetail.defaultSensorName', { pin: selectedSensor.pin }) })
                                            : t('deviceDetail.historyPrompt')}
                                    </p>
                                </div>
                                {sortedSensors.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-600">{t('deviceDetail.sensorLabel')}</label>
                                        <select
                                            value={selectedSensor?.id || ''}
                                            onChange={(e) => {
                                                const next = sortedSensors.find((sensor) => String(sensor.id) === e.target.value);
                                                if (next) {
                                                    setSelectedSensor(next);
                                                }
                                            }}
                                            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                                        >
                                            <option value="" disabled>{t('deviceDetail.historySelectPlaceholder')}</option>
                                            {sortedSensors.map((sensor) => (
                                                <option key={sensor.id} value={sensor.id}>
                                                    {sensor.name || t('deviceDetail.defaultSensorName', { pin: sensor.pin })}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {selectedSensor ? (
                                <HistoricalChart
                                    deviceId={id}
                                    sensorPin={selectedSensor.pin}
                                    sensorName={selectedSensor.name || t('deviceDetail.defaultSensorName', { pin: selectedSensor.pin })}
                                    sensorUnit={selectedSensor.unit || ''}
                                    timezone={device?.timezone}
                                />
                            ) : (
                                <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
                                    {t('deviceDetail.historyNoSensor')}
                                </div>
                            )}
                        </section>
                    </div>

                    <aside className="space-y-6">
                        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900">{t('deviceDetail.liveConnection')}</h3>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(device.status)}`}>
                                    {(device.status ? t(`deviceDetail.status.${device.status}`, { defaultValue: device.status }) : t('deviceDetail.status.unknown')).toUpperCase()}
                                </span>
                            </div>
                            <dl className="mt-4 space-y-3 text-sm">
                                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                                    <dt className="text-gray-500">{t('deviceDetail.deviceType')}</dt>
                                    <dd className="font-medium text-gray-900 uppercase">{device.device_type || 'ESP8266'}</dd>
                                </div>
                                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                                    <dt className="text-gray-500">{t('deviceDetail.lastHeartbeat')}</dt>
                                    <dd className="font-medium text-gray-900">{lastHeartbeatLabel}</dd>
                                </div>
                                <div className="flex items-center justify-between">
                                    <dt className="text-gray-500">{t('deviceDetail.targetFirmware')}</dt>
                                    <dd className="font-medium text-gray-900">{targetFirmware || t('deviceDetail.targetFirmwareNone')}</dd>
                                </div>
                            </dl>
                        </div>

                        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                                        <AlertTriangle className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{t('deviceDetail.alertsHeading')}</h3>
                                        <p className="text-xs text-gray-500">{activeAlerts.length} {t('deviceDetail.alertsCount', { count: activeAlerts.length })}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {activeAlerts.length === 0 ? (
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                                        <p className="text-sm text-gray-600">{t('deviceDetail.alertsEmpty')}</p>
                                    </div>
                                ) : (
                                    activeAlerts.map((alert) => {
                                        const severity = alert.severity?.toLowerCase?.() || 'low';
                                        return (
                                            <div
                                                key={alert.id || `${alert.alert_type}-${alert.triggered_at || alert.created_at}`}
                                                className={`rounded-lg border-l-4 p-4 transition-all hover:shadow-md ${severity === 'critical' ? 'border-red-500 bg-gradient-to-r from-red-50 to-red-100/50' :
                                                    severity === 'high' ? 'border-orange-500 bg-gradient-to-r from-orange-50 to-orange-100/50' :
                                                        severity === 'medium' ? 'border-yellow-500 bg-gradient-to-r from-yellow-50 to-yellow-100/50' :
                                                            'border-green-500 bg-gradient-to-r from-green-50 to-green-100/50'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                                            {alert.alert_type || t('deviceDetail.alerts.defaultType', 'Alert')}
                                                        </p>
                                                        <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                                            {alert.message || t('deviceDetail.alerts.noContext', 'No additional context')}
                                                        </p>
                                                        <p className="mt-2 text-xs text-gray-500">
                                                            {t('deviceDetail.triggeredAgo', {
                                                                when: formatRelativeTime(alert.triggered_at ? new Date(alert.triggered_at) : alert.created_at ? new Date(alert.created_at) : null)
                                                            })}
                                                        </p>
                                                    </div>
                                                    <span className={`flex-shrink-0 px-2 py-1 text-xs font-semibold rounded-full ${severityColors[severity] || 'bg-gray-100 text-gray-700'}`}>
                                                        {formatSeverityLabel(alert.severity)}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                            <div className="mt-4">
                                <Link
                                    to={`/alerts?device=${id}`}
                                    className="inline-flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors w-full justify-center"
                                >
                                    {t('deviceDetail.viewAllAlerts')}
                                </Link>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            {canManageDevice && showRuleEditor && selectedSensor && (
                <SensorRuleEditor
                    sensor={selectedSensor}
                    deviceId={id}
                    onClose={() => {
                        setShowRuleEditor(false);
                        setSelectedSensor(null);
                    }}
                />
            )}

            {canManageDevice && showOTAManager && (
                <OTAManager
                    device={device}
                    onClose={() => setShowOTAManager(false)}
                />
            )}

            {canManageDevice && showSensorManager && (
                <SensorManagerModal
                    device={device}
                    sensors={sortedSensors}
                    onClose={() => setShowSensorManager(false)}
                    onSave={() => {
                        queryClient.invalidateQueries(['device-sensors', id]);
                        setShowSensorManager(false);
                    }}
                />
            )}

            {canManageDevice && showDeviceSettings && (
                <DeviceSettingsModal
                    device={device}
                    config={deviceConfig}
                    onConfigChange={setDeviceConfig}
                    onClose={() => setShowDeviceSettings(false)}
                    onSave={handleSaveDeviceConfig}
                    isLoading={updateDeviceConfigMutation.isLoading}
                />
            )}

            {/* Location Change Modal */}
            {showLocationModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowLocationModal(false);
                            setTargetLocationId('');
                        }
                    }}
                >
                    <div
                        className="w-full max-w-md bg-white rounded-lg shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-b border-gray-200 p-6">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-indigo-600" />
                                {t('deviceDetail.changeLocation', 'Change Location')}
                            </h3>
                        </div>

                        <div className="p-6">
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                <div className="text-sm font-medium text-gray-900">{device?.name}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {t('deviceDetail.currentLocation', 'Current')}: {device?.location_name || t('common.none', 'None')}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('deviceDetail.selectNewLocation', 'Select New Location')}
                                </label>
                                <select
                                    value={targetLocationId}
                                    onChange={(e) => setTargetLocationId(e.target.value)}
                                    className="w-full rounded-md border-gray-300"
                                >
                                    <option value="">{t('common.none', 'None')}</option>
                                    {locations.map(location => (
                                        <option key={location.id} value={location.id}>
                                            {location.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 p-6 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowLocationModal(false);
                                    setTargetLocationId('');
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={() => {
                                    changeLocationMutation.mutate({
                                        deviceId: device.id,
                                        locationId: targetLocationId || null
                                    });
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                                disabled={changeLocationMutation.isLoading}
                            >
                                {changeLocationMutation.isLoading ? t('common.changing', 'Changing...') : t('common.change', 'Change')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DeviceSettingsModal({ device, config, onConfigChange, onClose, onSave, isLoading }) {
    const { t } = useTranslation();

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={onClose}
        >
            <div
                className="max-w-2xl w-full bg-white rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-gradient-to-br from-blue-200 via-indigo-200 to-purple-300 p-6 rounded-t-xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-indigo-900">{t('deviceDetail.deviceSettings', 'Device Settings')}</h2>
                            <p className="text-sm text-indigo-700 mt-1">
                                {device?.name} ({device?.id})
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-indigo-900 hover:bg-indigo-200 rounded-full p-2 transition-colors"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Heartbeat Interval */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('deviceDetail.heartbeatInterval', 'Heartbeat Interval')}
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="number"
                                min="10"
                                max="3600"
                                value={config.heartbeat_interval}
                                onChange={(e) => onConfigChange({ ...config, heartbeat_interval: parseInt(e.target.value) })}
                                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                            <span className="text-gray-600">{t('deviceDetail.seconds', 'seconds')}</span>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                            {t('deviceDetail.heartbeatHelp', 'How often the device sends status updates (10-3600 seconds). Lower = more real-time, higher = less network usage.')}
                        </p>
                        <div className="mt-2 text-xs text-gray-400">
                            <strong>{t('deviceDetail.recommended', 'Recommended')}:</strong> 30-300 seconds
                        </div>
                    </div>

                    {/* Sensor Read Interval */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('deviceDetail.sensorReadInterval', 'Sensor Read Interval')}
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="number"
                                min="100"
                                max="60000"
                                step="100"
                                value={config.sensor_read_interval}
                                onChange={(e) => onConfigChange({ ...config, sensor_read_interval: parseInt(e.target.value) })}
                                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                            />
                            <span className="text-gray-600">{t('deviceDetail.milliseconds', 'milliseconds')}</span>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                            {t('deviceDetail.sensorReadHelp', 'How often the device reads sensor values (100-60000 ms). Lower = more frequent readings, higher = better performance.')}
                        </p>
                        <div className="mt-2 text-xs text-gray-400">
                            <strong>{t('deviceDetail.recommended', 'Recommended')}:</strong> 1000-5000 ms (1-5 seconds)
                        </div>
                    </div>

                    {/* Armed Status */}
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <input
                            type="checkbox"
                            id="armed"
                            checked={config.armed}
                            onChange={(e) => onConfigChange({ ...config, armed: e.target.checked })}
                            className="mt-1 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1">
                            <label htmlFor="armed" className="block font-medium text-gray-900 cursor-pointer">
                                {t('deviceDetail.armedStatus', 'Armed Status')}
                            </label>
                            <p className="mt-1 text-sm text-gray-600">
                                {t('deviceDetail.armedHelp', 'When armed, the device will send alerts when thresholds are crossed. When disarmed, threshold checking is disabled.')}
                            </p>
                        </div>
                    </div>

                    {/* OTA Enabled */}
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <input
                            type="checkbox"
                            id="ota_enabled"
                            checked={config.ota_enabled}
                            onChange={(e) => onConfigChange({ ...config, ota_enabled: e.target.checked })}
                            className="mt-1 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1">
                            <label htmlFor="ota_enabled" className="block font-medium text-gray-900 cursor-pointer">
                                {t('deviceDetail.otaEnabled', 'OTA Updates Enabled')}
                            </label>
                            <p className="mt-1 text-sm text-gray-600">
                                {t('deviceDetail.otaHelp', 'Allow Over-The-Air firmware updates. Recommended to keep enabled for remote updates.')}
                            </p>
                        </div>
                    </div>

                    {/* Debug Mode */}
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                        <input
                            type="checkbox"
                            id="debug_mode"
                            checked={config.debug_mode}
                            onChange={(e) => onConfigChange({ ...config, debug_mode: e.target.checked })}
                            className="mt-1 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1">
                            <label htmlFor="debug_mode" className="block font-medium text-gray-900 cursor-pointer">
                                {t('deviceDetail.debugMode', 'Debug Mode')}
                            </label>
                            <p className="mt-1 text-sm text-gray-600">
                                {t('deviceDetail.debugHelp', 'Enable verbose logging in serial monitor. Useful for troubleshooting but increases serial output.')}
                            </p>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <div className="flex items-start gap-3">
                            <Activity className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <h4 className="font-medium text-blue-900">
                                    {t('deviceDetail.configUpdateInfo', 'Configuration Update')}
                                </h4>
                                <p className="mt-1 text-sm text-blue-700">
                                    {t('deviceDetail.configUpdateInfoText', 'Changes will be sent to the device on its next heartbeat (within {interval} seconds). The device will apply the new configuration automatically.', { interval: config.heartbeat_interval })}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                        disabled={isLoading}
                    >
                        {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                        onClick={onSave}
                        disabled={isLoading}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {t('common.saving', 'Saving...')}
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4" />
                                {t('common.save', 'Save Configuration')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DeviceDetail;
